import type { AppLocale } from "./translations.ts";

/**
 * Format integer hundredths exactly; Intl still owns grouping, signs, decimal
 * separators and non-SAR currency placement. Never convert cents to a float.
 */
export function formatMinorAmount(
  minor: number | null,
  locale: AppLocale,
  currency = "SAR",
  fractionDigits?: number,
): string | null {
  if (minor === null) return null;
  if (!Number.isSafeInteger(minor)) throw new RangeError("minor_units_invalid");
  const integerMinor = BigInt(minor);
  const negative = integerMinor < 0n;
  const magnitude = negative ? -integerMinor : integerMinor;
  const digits = fractionDigits ?? (magnitude % 100n === 0n ? 0 : 2);
  if (!Number.isInteger(digits) || digits < 0 || digits > 20) {
    throw new RangeError("fraction_digits_invalid");
  }

  // Preserve Intl's default half-expand rounding if a caller requests fewer
  // than two decimal places, but carry it out entirely in integer arithmetic.
  const divisor = digits < 2 ? 10n ** BigInt(2 - digits) : 1n;
  let scaled = magnitude / divisor;
  if (divisor > 1n && (magnitude % divisor) * 2n >= divisor) scaled += 1n;
  const scale = 10n ** BigInt(Math.min(digits, 2));
  const whole = scaled / scale;
  const fraction =
    digits === 0
      ? ""
      : (scaled % scale)
          .toString()
          .padStart(Math.min(digits, 2), "0")
          .padEnd(digits, "0");
  // -0 is intentional: amounts such as -0.01 need Intl's negative sign even
  // though their integer part is zero. All nonzero integers remain BigInts.
  const signedWhole = negative ? (whole === 0n ? -0 : -whole) : whole;
  const formatter = new Intl.NumberFormat(locale, {
    numberingSystem: "latn",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    ...(currency === "SAR"
      ? { style: "decimal" as const }
      : { style: "currency" as const, currency }),
  });
  return formatter
    .formatToParts(signedWhole)
    .map((part) => (part.type === "fraction" ? fraction : part.value))
    .join("");
}
